import React, { useEffect, useState, useMemo, useRef } from "react";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { useSearchParams } from "react-router-dom";
import PageHeader1 from "../../components/common/PageHeader1";
import { useDispatch, useSelector } from "react-redux";
import { fetchSessionReport } from "../../Redux/Reducers/InvoiceReportReducer";
import { formatUTCDateForDataGrid, formatUTCDateForExport, formatUTCDate, convertISTToUTCForAPI } from "../../utils/dateUtils";

const SessionReports = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: rowsData = [], paginationDetail = {}, status = "idle" } =
    useSelector((state) => state?.InvoiceReportReducer || {});

  const didInitUrlSyncRef = useRef(false);
  const applySawLoadingRef = useRef(false);
  const [isApplyingFilters, setIsApplyingFilters] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  // Helper function to get today's date in YYYY-MM-DD format
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayDate = getTodayDate();

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("astrologerName"); // "astrologerName", "userName", "mobile", "sessionType"
  const [appliedSearchType, setAppliedSearchType] = useState("astrologerName");
  const [startDate, setStartDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [lastAction, setLastAction] = useState(null); // "apply" to track when Apply Filters was clicked
  const [mobileNumber, setMobileNumber] = useState(null);
  const [activeTab, setActiveTab] = useState("completed"); // "completed", "rejected", "freeChat", "freeChatHuman", "aiAstrologer", "quit"
  const [rejectedData, setRejectedData] = useState([]);
  const [freeChatData, setFreeChatData] = useState([]);
  const [freeChatHumanData, setFreeChatHumanData] = useState([]);
  const [aiAstrologerData, setAiAstrologerData] = useState([]);
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [freeChatLoading, setFreeChatLoading] = useState(false);
  const [freeChatHumanLoading, setFreeChatHumanLoading] = useState(false);
  const [aiAstrologerLoading, setAiAstrologerLoading] = useState(false);
  const [rejectedPaginationDetail, setRejectedPaginationDetail] = useState({});
  const [freeChatPaginationDetail, setFreeChatPaginationDetail] = useState({});
  const [freeChatHumanPaginationDetail, setFreeChatHumanPaginationDetail] = useState({});
  const [aiAstrologerPaginationDetail, setAiAstrologerPaginationDetail] = useState({});
  const [quitData, setQuitData] = useState([]);
  const [quitLoading, setQuitLoading] = useState(false);
  const [quitPaginationDetail, setQuitPaginationDetail] = useState({});
  const isInitialMount = useRef(true);
  const lastParamsRef = useRef(null);
  const lastActiveTabRef = useRef(null);

  const redirectProcessed = useRef(false);

  const currentTabLoading =
    activeTab === "completed"
      ? status === "loading"
      : activeTab === "rejected"
        ? rejectedLoading
        : activeTab === "freeChat"
          ? freeChatLoading
          : activeTab === "freeChatHuman"
            ? freeChatHumanLoading
            : activeTab === "aiAstrologer"
              ? aiAstrologerLoading
              : quitLoading;

  const normalizeTab = (value) => {
    return value === "completed" || value === "rejected" || value === "freeChat" || value === "freeChatHuman" || value === "aiAstrologer" || value === "quit"
      ? value
      : "completed";
  };

  const normalizeSearchType = (value) => {
    const raw = String(value || "");
    const head = raw.split("=")[0]; // guards against malformed URLs like searchType=mobile=123...
    return head === "mobile" || head === "astrologerName" || head === "userName" || head === "sessionType"
      ? head
      : "astrologerName";
  };

  // Restore pagination + filters from URL on first mount (skip if redirect param exists)
  useEffect(() => {
    if (searchParams.get("redirect")) return;

    const tabParam = normalizeTab(searchParams.get("tab") || "");
    const pageParam = Number.parseInt(searchParams.get("page") || "0", 10);
    const pageSizeParam = Number.parseInt(searchParams.get("pageSize") || "10", 10);
    const searchParam = searchParams.get("search") || "";
    const searchTypeParam = normalizeSearchType(searchParams.get("searchType") || "astrologerName");
    const startDateParam = searchParams.get("startDate") || "";
    const endDateParam = searchParams.get("endDate") || "";

    setActiveTab(tabParam);

    if (Number.isFinite(pageParam) && pageParam >= 0) {
      setPaginationModel((prev) => ({ ...prev, page: pageParam }));
    }
    if (Number.isFinite(pageSizeParam) && pageSizeParam > 0) {
      setPaginationModel((prev) => ({ ...prev, pageSize: pageSizeParam }));
    }

    setSearchType(searchTypeParam);
    setAppliedSearchType(searchTypeParam);
    if (searchParam) {
      setSearchQuery(searchParam);
      setAppliedSearchQuery(searchParam);
    }
    if (startDateParam) {
      setStartDate(startDateParam);
      setAppliedStartDate(startDateParam);
    }
    if (endDateParam) {
      setEndDate(endDateParam);
      setAppliedEndDate(endDateParam);
    }

    const hasAnyUrlFilter = Boolean(searchParam) || Boolean(startDateParam) || Boolean(endDateParam);
    if (hasAnyUrlFilter) {
      setLastAction("apply");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for redirect parameter on mount and auto-search
  useEffect(() => {
    if (redirectProcessed.current) return;

    const redirectParam = searchParams.get('redirect');
    if (redirectParam) {
      redirectProcessed.current = true;
      setMobileNumber(redirectParam);
      setSearchQuery(redirectParam);
      setAppliedSearchQuery(redirectParam);
      setSearchType('mobile');
      setAppliedSearchType('mobile');
      // Keep lastAction as null so default date logic (today) still applies unless user explicitly sets dates.
      setLastAction(null);
      // Make the redirect filter durable and backend-friendly by pinning to today's date unless user changes it.
      setStartDate(todayDate);
      setEndDate(todayDate);
      setAppliedStartDate(todayDate);
      setAppliedEndDate(todayDate);
      setPaginationModel(prev => ({ ...prev, page: 0 }));

      // Replace redirect param with durable URL filters so it persists until reset/change.
      const newSearchParams = new URLSearchParams();
      newSearchParams.set('tab', 'completed');
      newSearchParams.set('page', '0');
      newSearchParams.set('pageSize', String(paginationModel.pageSize || 10));
      newSearchParams.set('searchType', 'mobile');
      newSearchParams.set('search', redirectParam);
      newSearchParams.set('startDate', todayDate);
      newSearchParams.set('endDate', todayDate);
      setSearchParams(newSearchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply Filters Handler - Applies search and date filters together
  const handleApplyFilters = () => {
    setIsApplyingFilters(true);
    applySawLoadingRef.current = false;
    setMobileNumber(null); // Clear mobileNumber when manually applying filters
    setAppliedSearchQuery(searchQuery.trim());
    setAppliedSearchType(searchType);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setLastAction("apply");
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Apply button should stay disabled until request completes.
  useEffect(() => {
    if (!isApplyingFilters) return;
    if (currentTabLoading) {
      applySawLoadingRef.current = true;
      return;
    }
    if (!currentTabLoading && applySawLoadingRef.current) {
      applySawLoadingRef.current = false;
      setIsApplyingFilters(false);
    }
  }, [currentTabLoading, isApplyingFilters]);

  // Keep URL in sync so Back restores pagination/filters.
  useEffect(() => {
    if (!didInitUrlSyncRef.current) {
      didInitUrlSyncRef.current = true;
      return;
    }

    const params = new URLSearchParams();
    params.set("tab", activeTab);
    params.set("page", String(paginationModel.page));
    params.set("pageSize", String(paginationModel.pageSize));

    if (appliedSearchQuery && appliedSearchQuery.trim()) {
      params.set("searchType", appliedSearchType);
      params.set("search", appliedSearchQuery.trim());
    }
    if (appliedStartDate) params.set("startDate", appliedStartDate);
    if (appliedEndDate) params.set("endDate", appliedEndDate);

    setSearchParams(params, { replace: true });
  }, [
    activeTab,
    paginationModel.page,
    paginationModel.pageSize,
    appliedSearchQuery,
    appliedSearchType,
    appliedStartDate,
    appliedEndDate,
    setSearchParams,
  ]);

  // Fetch data for Completed Sessions (with pagination)
  useEffect(() => {
    if (activeTab !== "completed") {
      // Update lastActiveTabRef when not on completed tab
      lastActiveTabRef.current = activeTab;
      return;
    }

    // Reset lastParamsRef when switching back to completed tab from another tab
    if (lastActiveTabRef.current && lastActiveTabRef.current !== "completed") {
      lastParamsRef.current = null;
    }
    lastActiveTabRef.current = "completed";

    // Skip initial load if redirect is being processed
    if (redirectProcessed.current && isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const page = paginationModel.page + 1;
    let limit = Number(paginationModel.pageSize);
    if (isNaN(limit)) {
      limit = 10000; // Use fixed default instead of paginationDetail
    }

    const params = {
      page: page,
      limit: limit,
    };

    // Date logic:
    // 1. If dates are applied (from Apply Filter), use them
    // 2. If no dates applied and it's initial load (lastAction is null), use today's date
    // 3. If no dates applied and Apply Filter was clicked (lastAction is "apply"), don't send dates
    if (appliedStartDate && appliedEndDate) {
      // Dates were explicitly applied - use them
      params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
      params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
    } else if (lastAction === null) {
      // Initial load or pagination change with no dates - send today's date by default
      params.startDate = convertISTToUTCForAPI(todayDate, 'start');
      params.endDate = convertISTToUTCForAPI(todayDate, 'end');
    }
    // If lastAction is "apply" and no dates, don't send dates (params won't have startDate/endDate)

    // If mobileNumber is present (from redirect), use it directly, otherwise use search
    if (mobileNumber) {
      params.mobileNumber = mobileNumber;
    } else if (appliedSearchQuery && appliedSearchQuery.trim()) {
      params.search = appliedSearchQuery;
      params.searchType = appliedSearchType;
    }

    // Prevent duplicate API calls by comparing params
    const paramsString = JSON.stringify(params);
    if (lastParamsRef.current === paramsString && !isInitialMount.current) {
      return; // Skip if params haven't changed
    }

    lastParamsRef.current = paramsString;
    isInitialMount.current = false;

    dispatch(fetchSessionReport(params));
  }, [dispatch, paginationModel.page, paginationModel.pageSize, appliedStartDate, appliedEndDate, appliedSearchQuery, appliedSearchType, lastAction, todayDate, mobileNumber, activeTab]);

  // Fetch data for Rejected Sessions
  useEffect(() => {
    if (activeTab !== "rejected") return;

    const fetchRejectedData = async () => {
      setRejectedLoading(true);
      try {
        const token = localStorage.getItem("astroVani-admin-token");
        const params = new URLSearchParams();

        const exportStartDate = appliedStartDate || todayDate;
        const exportEndDate = appliedEndDate || todayDate;
        // Add pagination parameters
        const page = paginationModel.page + 1;
        const limit = paginationModel.pageSize;
        params.append('page', page);
        params.append('limit', limit);
        params.append('startDate', convertISTToUTCForAPI(exportStartDate, 'start'));
        params.append('endDate', convertISTToUTCForAPI(exportEndDate, 'end'));

        // Add mobileNumber if present (from redirect)
        if (mobileNumber) {
          params.append('mobileNumber', mobileNumber);
        } else if (appliedSearchQuery && appliedSearchQuery.trim()) {
          // Add search parameters based on search type
          if (appliedSearchType === "mobile") {
            params.append('mobileNumber', appliedSearchQuery);
          } else if (appliedSearchType === "astrologerName") {
            params.append('astrologerName', appliedSearchQuery);
          } else if (appliedSearchType === "userName") {
            params.append('userName', appliedSearchQuery);
          } else if (appliedSearchType === "sessionType") {
            params.append('sessionType', appliedSearchQuery);
          }
        }

        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}/admin/sessionReportRejected?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch rejected sessions");
        }

        const result = await response.json();
        const data = Array.isArray(result) ? result : result?.data || [];
        setRejectedData(data);

        // Set pagination details if available
        if (result?.paginationDetail) {
          setRejectedPaginationDetail(result.paginationDetail);
        } else if (result?.totalRecords !== undefined) {
          setRejectedPaginationDetail({ totalRecords: result.totalRecords });
        } else {
          setRejectedPaginationDetail({ totalRecords: data.length });
        }
      } catch (error) {
        console.error("Error fetching rejected sessions:", error);
        setRejectedData([]);
        setRejectedPaginationDetail({});
      } finally {
        setRejectedLoading(false);
      }
    };

    fetchRejectedData();
  }, [activeTab, appliedStartDate, appliedEndDate, todayDate, mobileNumber, appliedSearchQuery, appliedSearchType, paginationModel.page, paginationModel.pageSize]);

  // Fetch data for Free Chat Sessions
  useEffect(() => {
    if (activeTab !== "freeChat") return;

    const fetchFreeChatData = async () => {
      setFreeChatLoading(true);
      try {
        const token = localStorage.getItem("astroVani-admin-token");
        const params = new URLSearchParams();

        const exportStartDate = appliedStartDate || todayDate;
        const exportEndDate = appliedEndDate || todayDate;
        // Add pagination parameters
        const page = paginationModel.page + 1;
        const limit = paginationModel.pageSize;
        params.append('page', page);
        params.append('limit', limit);
        params.append('startDate', convertISTToUTCForAPI(exportStartDate, 'start'));
        params.append('endDate', convertISTToUTCForAPI(exportEndDate, 'end'));

        // Add mobileNumber if present (from redirect)
        if (mobileNumber) {
          params.append('mobileNumber', mobileNumber);
        } else if (appliedSearchQuery && appliedSearchQuery.trim()) {
          // Add search parameters based on search type
          if (appliedSearchType === "mobile") {
            params.append('mobileNumber', appliedSearchQuery);
          } else if (appliedSearchType === "astrologerName") {
            params.append('astrologerName', appliedSearchQuery);
          } else if (appliedSearchType === "userName") {
            params.append('userName', appliedSearchQuery);
          } else if (appliedSearchType === "sessionType") {
            params.append('sessionType', appliedSearchQuery);
          }
        }

        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}/admin/sessionReportFreeChat?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch free chat sessions");
        }

        const result = await response.json();
        const data = Array.isArray(result) ? result : result?.data || [];
        setFreeChatData(data);

        // Set pagination details if available
        if (result?.paginationDetail) {
          setFreeChatPaginationDetail(result.paginationDetail);
        } else if (result?.totalRecords !== undefined) {
          setFreeChatPaginationDetail({ totalRecords: result.totalRecords });
        } else {
          setFreeChatPaginationDetail({ totalRecords: data.length });
        }
      } catch (error) {
        console.error("Error fetching free chat sessions:", error);
        setFreeChatData([]);
        setFreeChatPaginationDetail({});
      } finally {
        setFreeChatLoading(false);
      }
    };

    fetchFreeChatData();
  }, [activeTab, appliedStartDate, appliedEndDate, todayDate, mobileNumber, appliedSearchQuery, appliedSearchType, paginationModel.page, paginationModel.pageSize]);

  // Fetch data for Free Human Chat Sessions
  useEffect(() => {
    if (activeTab !== "freeChatHuman") return;

    const fetchFreeChatHumanData = async () => {
      setFreeChatHumanLoading(true);
      try {
        const token = localStorage.getItem("astroVani-admin-token");
        const params = new URLSearchParams();

        const exportStartDate = appliedStartDate || todayDate;
        const exportEndDate = appliedEndDate || todayDate;
        // Add pagination parameters
        const page = paginationModel.page + 1;
        const limit = paginationModel.pageSize;
        params.append('page', page);
        params.append('limit', limit);
        params.append('startDate', convertISTToUTCForAPI(exportStartDate, 'start'));
        params.append('endDate', convertISTToUTCForAPI(exportEndDate, 'end'));

        // Add mobileNumber if present (from redirect)
        if (mobileNumber) {
          params.append('mobileNumber', mobileNumber);
        } else if (appliedSearchQuery && appliedSearchQuery.trim()) {
          // Add search parameters based on search type
          if (appliedSearchType === "mobile") {
            params.append('mobileNumber', appliedSearchQuery);
          } else if (appliedSearchType === "astrologerName") {
            params.append('astrologerName', appliedSearchQuery);
          } else if (appliedSearchType === "userName") {
            params.append('userName', appliedSearchQuery);
          } else if (appliedSearchType === "sessionType") {
            params.append('sessionType', appliedSearchQuery);
          }
        }

        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}/admin/sessionReportFreeChatHuman?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch free human chat sessions");
        }

        const result = await response.json();
        const data = Array.isArray(result) ? result : result?.data || [];
        setFreeChatHumanData(data);

        // Set pagination details if available
        if (result?.paginationDetail) {
          setFreeChatHumanPaginationDetail(result.paginationDetail);
        } else if (result?.totalRecords !== undefined) {
          setFreeChatHumanPaginationDetail({ totalRecords: result.totalRecords });
        } else {
          setFreeChatHumanPaginationDetail({ totalRecords: data.length });
        }
      } catch (error) {
        console.error("Error fetching free human chat sessions:", error);
        setFreeChatHumanData([]);
        setFreeChatHumanPaginationDetail({});
      } finally {
        setFreeChatHumanLoading(false);
      }
    };

    fetchFreeChatHumanData();
  }, [activeTab, appliedStartDate, appliedEndDate, todayDate, mobileNumber, appliedSearchQuery, appliedSearchType, paginationModel.page, paginationModel.pageSize]);

  // Fetch data for Ai Astrologer Sessions
  useEffect(() => {
    if (activeTab !== "aiAstrologer") return;

    const fetchAiAstrologerData = async () => {
      setAiAstrologerLoading(true);
      try {
        const token = localStorage.getItem("astroVani-admin-token");
        const params = new URLSearchParams();

        // Add pagination parameters
        const page = paginationModel.page + 1;
        const limit = paginationModel.pageSize;
        params.append('page', page);
        params.append('limit', limit);

        // Date logic: use applied dates or today's date
        const exportStartDate = appliedStartDate || todayDate;
        const exportEndDate = appliedEndDate || todayDate;
        params.append('startDate', convertISTToUTCForAPI(exportStartDate, 'start'));
        params.append('endDate', convertISTToUTCForAPI(exportEndDate, 'end'));

        // Add userId if present (from redirect)
        if (mobileNumber) {
          params.append('mobileNumber', mobileNumber);
        } else if (appliedSearchQuery && appliedSearchQuery.trim()) {
          // Add search parameters based on search type
          if (appliedSearchType === "mobile") {
            params.append('mobileNumber', appliedSearchQuery);
          } else if (appliedSearchType === "astrologerName") {
            params.append('astrologerName', appliedSearchQuery);
          } else if (appliedSearchType === "userName") {
            params.append('userName', appliedSearchQuery);
          } else if (appliedSearchType === "sessionType") {
            params.append('sessionType', appliedSearchQuery);
          }
        }

        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}/admin/sessionAiAstrologer?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch AI astrologer sessions");
        }

        const result = await response.json();
        const data = Array.isArray(result) ? result : result?.data || [];
        setAiAstrologerData(data);

        // Set pagination details if available
        if (result?.paginationDetail) {
          setAiAstrologerPaginationDetail(result.paginationDetail);
        } else if (result?.totalRecords !== undefined) {
          setAiAstrologerPaginationDetail({ totalRecords: result.totalRecords });
        } else {
          setAiAstrologerPaginationDetail({ totalRecords: data.length });
        }
      } catch (error) {
        console.error("Error fetching AI astrologer sessions:", error);
        setAiAstrologerData([]);
        setAiAstrologerPaginationDetail({});
      } finally {
        setAiAstrologerLoading(false);
      }
    };

    fetchAiAstrologerData();
  }, [activeTab, appliedStartDate, appliedEndDate, todayDate, mobileNumber, appliedSearchQuery, appliedSearchType, paginationModel.page, paginationModel.pageSize]);

  // Fetch data for Quited Sessions
  useEffect(() => {
    if (activeTab !== "quit") return;

    const fetchQuitData = async () => {
      setQuitLoading(true);
      try {
        const token = localStorage.getItem("astroVani-admin-token");
        const params = new URLSearchParams();

        // Add pagination parameters
        const page = paginationModel.page + 1;
        const limit = paginationModel.pageSize;
        params.append('page', page);
        params.append('limit', limit);

        // Date logic: use applied dates or today's date
        const exportStartDate = appliedStartDate || todayDate;
        const exportEndDate = appliedEndDate || todayDate;
        params.append('startDate', convertISTToUTCForAPI(exportStartDate, 'start'));
        params.append('endDate', convertISTToUTCForAPI(exportEndDate, 'end'));

        if (mobileNumber) {
          params.append('mobileNumber', mobileNumber);
        } else if (appliedSearchQuery && appliedSearchQuery.trim()) {
          if (appliedSearchType === "mobile") {
            params.append('mobileNumber', appliedSearchQuery);
          } else if (appliedSearchType === "astrologerName") {
            params.append('astrologerName', appliedSearchQuery);
          } else if (appliedSearchType === "userName") {
            params.append('userName', appliedSearchQuery);
          } else if (appliedSearchType === "sessionType") {
            params.append('sessionType', appliedSearchQuery);
          }
        }

        const response = await fetch(
          `${process.env.REACT_APP_BASEURL}/admin/sessionQuit?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch quited sessions");
        }

        const result = await response.json();
        const data = Array.isArray(result) ? result : result?.data || [];
        setQuitData(data);

        if (result?.paginationDetail) {
          setQuitPaginationDetail(result.paginationDetail);
        } else if (result?.totalRecords !== undefined) {
          setQuitPaginationDetail({ totalRecords: result.totalRecords });
        } else {
          setQuitPaginationDetail({ totalRecords: data.length });
        }
      } catch (error) {
        console.error("Error fetching quited sessions:", error);
        setQuitData([]);
        setQuitPaginationDetail({});
      } finally {
        setQuitLoading(false);
      }
    };

    fetchQuitData();
  }, [activeTab, appliedStartDate, appliedEndDate, todayDate, mobileNumber, appliedSearchQuery, appliedSearchType, paginationModel.page, paginationModel.pageSize]);

  const convertSecondsToTime = (totalSeconds = 0) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours} hr ${minutes} min ${seconds} sec`;
  };

  const formatCamelCase = (str = "") =>
    str
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/^./, (char) => char.toUpperCase());

  const rows = useMemo(() => {
    let dataToUse = [];
    if (activeTab === "completed") {
      dataToUse = rowsData || [];
    } else if (activeTab === "rejected") {
      dataToUse = rejectedData || [];
    } else if (activeTab === "freeChat") {
      dataToUse = freeChatData || [];
    } else if (activeTab === "freeChatHuman") {
      dataToUse = freeChatHumanData || [];
    } else if (activeTab === "aiAstrologer") {
      dataToUse = aiAstrologerData || [];
    } else if (activeTab === "quit") {
      dataToUse = quitData || [];
    }

    const pageSize = paginationModel.pageSize;
    return dataToUse
      ?.map((item, index) => ({
        ...item,
        id: item._id || item.id || index,
        sono: index + 1 + paginationModel.page * pageSize,
        astrologerName: item?.astrologerName && item?.astrologerName !== "N/A" ? item?.astrologerName : "Deleted Astrologer",
        userName: item?.userName && item?.userName !== "N/A" ? item?.userName : "Deleted User",
        mobileNumber: item?.mobileNumber && item?.mobileNumber !== "N/A" ? item?.mobileNumber : "-",
        refundedAmount: item?.refundedAmount ? item?.refundedAmount || 0 : 0,
        sessionType: formatCamelCase(item?.sessionType),
        duration: convertSecondsToTime(item?.duration),
        cashbackAmount: item?.cashbackAmount || 0,
        cashbackType: item?.cashbackType || "-",
        cashbackValue: item?.cashbackValue || 0,
        isFreeChat: item?.isFreeChat !== undefined ? Boolean(item?.isFreeChat) : false,
      }))
  }, [rowsData, rejectedData, freeChatData, freeChatHumanData, aiAstrologerData, quitData, paginationModel, activeTab]);

  const columns = useMemo(() => [
    { field: "sono", headerName: "S.No", width: 80, align: "center", headerAlign: "center" },
    { field: "astrologerName", headerName: "Astrologer Name", width: 180 },
    { field: "userName", headerName: "User Name", width: 160 },
    { field: "mobileNumber", headerName: "Mobile No.", width: 150 },
    { field: "sessionType", headerName: "Call/Chat", width: 120 },
    { field: "endBy", headerName: "End By", width: 150, valueFormatter: (value) => value ? formatCamelCase(value) : "-" },
    { field: "session", headerName: "Session", width: 150 },
    ...(activeTab === "completed"
      ? [{ field: "isConvertedFromFreeChat", headerName: "Converted From Free Chat", width: 200, valueFormatter: (value) => value ? "Yes" : "No" }]
      : []),
    ...(activeTab === "rejected" || activeTab === "quit"
      ? [{ field: "isFreeChat", headerName: "Free Chat", width: 140, valueFormatter: (value) => value ? "Yes" : "No" }]
      : []),
    { field: "duration", headerName: "Duration", width: 140 },
    { field: "walletBefore", headerName: "Wallet Before", width: 150, valueFormatter: (value) => value ? value.toFixed(2) : "0.00" },
    { field: "walletAmount", headerName: "Wallet Deduction on Consultation", width: 180, valueFormatter: (value) => value ? value.toFixed(2) : "0.00" },
    { field: "walletAfter", headerName: "Wallet After", width: 150, valueFormatter: (value) => value ? value.toFixed(2) : "0.00" },
    { field: "astroShare", headerName: "Astro Share", width: 140 },
    { field: "cashbackAmount", headerName: "Cashback Received", width: 140, valueFormatter: (value) => value ? value.toFixed(2) : "0.00" },
    { field: "cashbackType", headerName: "Cashback Type", width: 140 },
    { field: "cashbackValue", headerName: "Cashback Value", width: 140, valueFormatter: (value) => value ? value.toFixed(2) : "0.00" },
    { field: "orderId", headerName: "Booking Id", width: 140 },
    { field: "rechargeDuringSession", headerName: "Recharge During Session", width: 180 },
    { field: "isRefundedAmount", headerName: "Refunded Amount", width: 140, valueFormatter: (value) => value ? "Yes" : "No" },
    { field: "refundedAmount", headerName: "Amount Refunded", width: 140, valueFormatter: (value) => value ? value.toFixed(2) : "0.00" },
    // { field: "promotionShare", headerName: "Promotion Share", width: 160 },
    { field: "companyShare", headerName: "Company Share", width: 160 },
    { field: "createdAt", headerName: "Created At", width: 180, valueFormatter: formatUTCDateForDataGrid },
    { field: "updatedAt", headerName: "Updated At", width: 180, valueFormatter: formatUTCDateForDataGrid },
  ], [activeTab]);

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");

      // Build query parameters for export - include search if present
      const params = new URLSearchParams();
      // Send dates - use today's date if not provided
      const exportStartDate = appliedStartDate || todayDate;
      const exportEndDate = appliedEndDate || todayDate;
      params.append('startDate', convertISTToUTCForAPI(exportStartDate, 'start'));
      params.append('endDate', convertISTToUTCForAPI(exportEndDate, 'end'));

      // Determine API endpoint based on active tab
      let apiEndpoint = '/admin/sessionReportExport';
      if (activeTab === "rejected") {
        apiEndpoint = '/admin/sessionReportExportRejected';
      } else if (activeTab === "freeChat") {
        apiEndpoint = '/admin/sessionReportExportFreeChat';
      } else if (activeTab === "freeChatHuman") {
        apiEndpoint = '/admin/sessionReportExportFreeChatHuman';
      } else if (activeTab === "aiAstrologer") {
        apiEndpoint = '/admin/sessionAiAstrologerExport';
      } else if (activeTab === "quit") {
        apiEndpoint = '/admin/sessionQuitExport';
      }

      // Include search parameters in export if search was applied (for all tabs)
      if (mobileNumber) {
        params.append('mobileNumber', mobileNumber);
      } else if (appliedSearchQuery && appliedSearchQuery.trim()) {
        if (appliedSearchType === "astrologerName") {
          params.append('astrologerName', appliedSearchQuery);
        } else if (appliedSearchType === "userName") {
          params.append('userName', appliedSearchQuery);
        } else if (appliedSearchType === "mobile") {
          params.append('mobileNumber', appliedSearchQuery);
        } else if (appliedSearchType === "sessionType") {
          params.append('sessionType', appliedSearchQuery);
        }
      }

      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}${apiEndpoint}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized: Please login again");
        }
        throw new Error("Failed to export data");
      }

      const result = await response.json();

      const exportRows = Array.isArray(result) ? result : result?.data || [];

      if (!Array.isArray(exportRows) || exportRows.length === 0) {
        alert("No data available to export");
        setIsExporting(false);
        return;
      }

      const convertSecondsToTime = (totalSeconds = 0) => {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        return `${hours} hr ${minutes} min ${seconds} sec`;
      };

      const formatCamelCase = (str = "") =>
        str
          .replace(/([a-z])([A-Z])/g, "$1 $2")
          .replace(/^./, (char) => char.toUpperCase());

      const exportData = exportRows.map((row, index) => ({
        "S.No": index + 1,
        "Astrologer Name": row.astrologerName && row?.astrologerName !== "N/A" ? row?.astrologerName : "Deleted Astrologer",
        "User Name": row.userName && row?.userName !== "N/A" ? row?.userName : "Deleted User",
        "Mobile No.": row.mobileNumber && row?.mobileNumber !== "N/A" ? row?.mobileNumber : "-",
        "Call/Chat": formatCamelCase(row.sessionType || ""),
        "End By": row.endBy ? formatCamelCase(row.endBy) : "-",
        "Session": row.session || "",
        ...(activeTab === "completed" ? { "Converted From Free Chat": row.isConvertedFromFreeChat ? "Yes" : "No" } : {}),
        ...((activeTab === "rejected" || activeTab === "quit") ? { "Free Chat": (row.isFreeChat !== undefined ? Boolean(row.isFreeChat) : false) ? "Yes" : "No" } : {}),
        "Duration": convertSecondsToTime(row.duration),
        "Wallet Before": +(row.walletBefore || 0).toFixed(2),
        "Wallet Deduction on Consultation": +(row.walletAmount || 0).toFixed(2),
        "Wallet After": +(row.walletAfter || 0).toFixed(2),
        "Astro Share": +(row.astroShare || 0).toFixed(2),
        "Cashback Received": +(row.cashbackAmount || 0).toFixed(2),
        "Cashback Type": row.cashbackType || "-",
        "Cashback Value": +(row.cashbackValue || 0).toFixed(2),
        "Promotion Share": +(row.promotionShare || 0).toFixed(2),
        "Recharge During Session": +(row.rechargeDuringSession || 0),
        "Refunded Amount": row.isRefundedAmount ? "Yes" : "No",
        "Amount Refunded": +(row.refundedAmount || 0).toFixed(2),
        "Company Share": +(row.companyShare || 0).toFixed(2),
        "Booking Id": row.orderId || "",
        "Created Date": formatUTCDate(row.createdAt, { format: 'date', separator: '/' }),
        "Created Time": formatUTCDate(row.createdAt, { format: 'time', showSeconds: true }),
        "Updated Date": formatUTCDate(row.updatedAt, { format: 'date', separator: '/' }),
        "Updated Time": formatUTCDate(row.updatedAt, { format: 'time', showSeconds: true }),
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "SessionReports");
      XLSX.writeFile(workbook, `Session_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error("Export error:", error);
      alert(error.message || "Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };
  const handleClose = () => setOpen(false);
  const handleDelete = () => {
    setOpen(false);
  };

  // 🧭 Pagination Handlers
  const handleChangePage = (event, newPage) =>
    setPaginationModel((prev) => ({ ...prev, page: newPage }));

  const handleChangeRowsPerPage = (event) => {
    const value = event.target.value;
    const newSize = parseInt(value, 10);
    setPaginationModel({ page: 0, pageSize: newSize });
  };

  // Reset Filter Handler
  const handleResetFilter = () => {
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setSearchQuery("");
    setAppliedSearchQuery("");
    setSearchType("astrologerName");
    setAppliedSearchType("astrologerName");
    setMobileNumber(null);
    setLastAction(null);
    setActiveTab("completed");
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Session Reports" />

        {/* Search and Export Toolbar */}
        <Box sx={{ width: '100%', mb: 2 }}>
          {/* Top Line: Search Items + Date Filters */}
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={2}>
            <FormControl size="small" sx={{ minWidth: 140, flexShrink: 0 }}>
              <InputLabel>Search By</InputLabel>
              <Select
                value={searchType}
                label="Search By"
                onChange={(e) => setSearchType(e.target.value)}
                sx={{
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-input': {
                    padding: '6px 14px',
                  },
                  '& .MuiInputBase-root': {
                    height: '32px',
                  }
                }}
              >
                <MenuItem value="astrologerName">Astrologer Name</MenuItem>
                <MenuItem value="userName">User Name</MenuItem>
                <MenuItem value="mobile">Mobile</MenuItem>
                <MenuItem value="sessionType">Session Type</MenuItem>
              </Select>
            </FormControl>
            <TextField
              placeholder={
                searchType === "astrologerName" ? "Search by astrologer name..." :
                  searchType === "userName" ? "Search by user name..." :
                    searchType === "mobile" ? "Search by mobile..." :
                      "Search by session type..."
              }
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => { if (e.key === 'Enter') { handleApplyFilters(); } }}
              sx={{
                width: { xs: '150px', sm: '180px', md: '200px' },
                flexShrink: 0,
                background: 'var(--card-color)',
                borderRadius: '9px',
                '& .MuiInputBase-input': {
                  fontSize: '13px',
                  padding: '6px 10px 6px 0',
                  height: '20px'
                },
                '& .MuiInputBase-root': {
                  height: '32px',
                }
              }}
            />
            <TextField
              label="Start Date"
              type="date"
              variant="outlined"
              size="small"
              value={startDate || ""}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: { xs: '140px', sm: '160px', md: '180px' },
                flexShrink: 0,
                '& .MuiInputBase-input[type="date"]': {
                  paddingRight: '8px',
                  paddingLeft: '14px',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  height: '20px',
                  position: 'relative',
                  '&::-webkit-calendar-picker-indicator': {
                    cursor: 'pointer',
                    padding: '2px',
                    marginLeft: '0px',
                    marginRight: '4px',
                    opacity: 0.7,
                    position: 'absolute',
                    right: 0,
                    '&:hover': {
                      opacity: 1,
                    },
                  },
                },
                '& .MuiOutlinedInput-root': {
                  paddingRight: '8px',
                  height: '32px',
                },
              }}
            />
            <TextField
              label="End Date"
              type="date"
              variant="outlined"
              size="small"
              value={endDate || ""}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                width: { xs: '140px', sm: '160px', md: '180px' },
                flexShrink: 0,
                '& .MuiInputBase-input[type="date"]': {
                  paddingRight: '8px',
                  paddingLeft: '14px',
                  paddingTop: '6px',
                  paddingBottom: '6px',
                  height: '20px',
                  position: 'relative',
                  '&::-webkit-calendar-picker-indicator': {
                    cursor: 'pointer',
                    padding: '2px',
                    marginLeft: '0px',
                    marginRight: '4px',
                    opacity: 0.7,
                    position: 'absolute',
                    right: 0,
                    '&:hover': {
                      opacity: 1,
                    },
                  },
                },
                '& .MuiOutlinedInput-root': {
                  paddingRight: '8px',
                  height: '32px',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={
                currentTabLoading ||
                isApplyingFilters ||
                (!searchQuery.trim() && !startDate && !endDate)
              }
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                backgroundColor: "#E7B242",
                color: "#fff",
                height: 32,
                px: 2,
                flexShrink: 0,
                fontSize: '13px',
                "&:hover": { backgroundColor: "#E7B242", opacity: 0.9 },
                "&:disabled": {
                  backgroundColor: "rgba(0, 0, 0, 0.12)",
                  color: "rgba(0, 0, 0, 0.26)",
                },
              }}
            >
              {isApplyingFilters ? "Applying..." : "Apply Filters"}
            </Button>
          </Box>
          {/* Bottom Line: Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              onClick={handleResetFilter}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                borderColor: '#E7B242',
                color: '#E7B242',
                height: 32,
                px: 2,
                fontSize: '13px',
                '&:hover': { borderColor: '#E7B242', backgroundColor: 'rgba(231, 178, 66, 0.08)' },
                flexShrink: 0
              }}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={exportToExcel}
              disabled={isExporting}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                backgroundColor: "#E7B242",
                color: "#fff",
                height: 32,
                px: 2,
                fontSize: '13px',
                "&:hover": { backgroundColor: "#E7B242", opacity: 0.9 },
                "&:disabled": { backgroundColor: "#E7B242", opacity: 0.6 },
              }}
            >
              {isExporting ? (
                <>
                  <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                  Exporting...
                </>
              ) : (
                "Export to Excel"
              )}
            </Button>
          </Box>
        </Box>
        <Box>
          <Tabs
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            value={activeTab}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              setPaginationModel(prev => ({ ...prev, page: 0 }));
            }}
            sx={{
              minHeight: '25px',
              marginBottom: '5px',
              padding: '0px 0px',
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                display: 'none',
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: '25px',
                padding: '10px 10px', // Reduced padding
                minWidth: 'auto', // Allow it to shrink
              },
              '& .Mui-selected': {
                color: '#E7B242',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#E7B242',
              },
            }}
          >
            <Tab label="Completed Sessions" value="completed" />
            <Tab label="Rejected Sessions" value="rejected" />
            <Tab label="Free AI Chat Sessions" value="freeChat" />
            <Tab label="Free Human Chat Sessions" value="freeChatHuman" />
            <Tab label="AI Astrologer Sessions" value="aiAstrologer" />
            <Tab label="Quit Sessions" value="quit" />
          </Tabs>
        </Box>

        {/* 🧾 DataGrid Section */}
        <div
          className="card"
          style={{
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <div className="card-body" style={{ padding: 0 }}>
            {/* 👇 Tabs and Pagination on Top */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #e9ecef",
                px: 2,
              }}
            >
              <TablePagination
                component="div"
                count={
                  activeTab === "completed"
                    ? (paginationDetail?.totalRecords || 0)
                    : activeTab === "rejected"
                      ? (rejectedPaginationDetail?.totalRecords || 0)
                      : activeTab === "freeChat"
                        ? (freeChatPaginationDetail?.totalRecords || 0)
                        : activeTab === "freeChatHuman"
                          ? (freeChatHumanPaginationDetail?.totalRecords || 0)
                          : activeTab === "aiAstrologer"
                            ? (aiAstrologerPaginationDetail?.totalRecords || 0)
                            : (quitPaginationDetail?.totalRecords || 0)
                }
                page={paginationModel.page}
                onPageChange={handleChangePage}
                rowsPerPage={paginationModel.pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Box>

            <Box
              sx={{
                width: "100%",
                "& .MuiDataGrid-root": { border: "none" },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f8f9fa",
                  borderBottom: "2px solid #e9ecef",
                  fontWeight: 600,
                },
              }}
            >
              <DataGrid
                autoHeight
                columns={columns}
                rows={rows}
                getRowId={(row) => row.id}
                rowCount={
                  activeTab === "completed"
                    ? (paginationDetail?.totalRecords || 0)
                    : activeTab === "rejected"
                      ? (rejectedPaginationDetail?.totalRecords || 0)
                      : activeTab === "freeChat"
                        ? (freeChatPaginationDetail?.totalRecords || 0)
                        : activeTab === "freeChatHuman"
                          ? (freeChatHumanPaginationDetail?.totalRecords || 0)
                          : activeTab === "aiAstrologer"
                            ? (aiAstrologerPaginationDetail?.totalRecords || 0)
                            : (quitPaginationDetail?.totalRecords || 0)
                }
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                paginationMode="server"
                loading={status === "loading" || rejectedLoading || freeChatLoading || freeChatHumanLoading || aiAstrologerLoading || quitLoading}
                disableRowSelectionOnClick
                hideFooterPagination
                hideFooter
                components={{
                  LoadingOverlay: CircularProgress,
                }}
                sx={{
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                }}
              />
            </Box>
          </div>
        </div>
      </div>

      {/* 🗑️ Delete Confirmation Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this session record?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SessionReports;
